// covered-by: frontends/carrie/src/ask-v2/home/__tests__/ActionStack.test.tsx
import {
  type AskPunttActionCard,
  type AskPunttActionResolution,
} from "@mg/schemas/src/christo/askPunttBriefing";
import { Button, Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import { canRenderApprove } from "./canRenderApprove";

type ActionCardProps = {
  card: AskPunttActionCard;
  expanded: boolean;
  resolving: boolean;
  onExpand: () => void;
  onResolve: (resolution: AskPunttActionResolution) => void;
};

export function ActionCard({
  card,
  expanded,
  resolving,
  onExpand,
  onResolve,
}: ActionCardProps) {
  const { t } = useTranslation("ui");
  const approve = canRenderApprove(card);

  return (
    <article
      data-testid="ask-v2-action-card"
      data-action-id={card.id}
      data-expanded={expanded ? "true" : "false"}
      data-confidence={card.confidence}
      className="flex flex-col gap-3 rounded-2xl bg-base-white p-4 ring-1 ring-puntt-neutral-gray-6"
    >
      <button
        type="button"
        className="text-left"
        data-testid="ask-v2-action-title"
        onClick={onExpand}
      >
        <Text as="p" size="3" weight="medium">
          {card.title}
        </Text>
      </button>
      {expanded ? (
        <>
          <Text as="p" size="2">
            <span className="font-medium">{t("askV2.home.why")}: </span>
            {card.rationale}
          </Text>
          <Text as="p" size="2">
            <span className="font-medium">{t("askV2.home.impact")}: </span>
            {card.impact}
          </Text>
          <Text as="p" size="2" color="gray">
            {card.reversible
              ? t("askV2.home.reversible")
              : t("askV2.home.notReversible")}
          </Text>
          <div className="flex flex-wrap gap-2">
            <Button
              size="2"
              data-testid={approve ? "ask-v2-approve" : "ask-v2-preview"}
              disabled={resolving || !approve}
              onClick={() => {
                if (!approve) return;
                onResolve("approved");
              }}
            >
              {approve ? t("askV2.home.approve") : t("askV2.home.preview")}
            </Button>
            {card.secondary.includes("digDeeper") ? (
              <Button
                size="2"
                variant="soft"
                data-testid="ask-v2-dig-deeper"
                disabled={resolving}
                onClick={() => onResolve("dig_deeper")}
              >
                {t("askV2.home.digDeeper")}
              </Button>
            ) : null}
            {card.secondary.includes("notMine") ? (
              <Button
                size="2"
                variant="ghost"
                data-testid="ask-v2-not-mine"
                disabled={resolving}
                onClick={() => onResolve("not_mine")}
              >
                {t("askV2.home.notMine")}
              </Button>
            ) : null}
            {card.secondary.includes("notNow") ? (
              <Button
                size="2"
                variant="ghost"
                data-testid="ask-v2-not-now"
                disabled={resolving}
                onClick={() => onResolve("not_now")}
              >
                {t("askV2.home.notNow")}
              </Button>
            ) : null}
          </div>
        </>
      ) : null}
    </article>
  );
}
